import { from, Observable, of, ReplaySubject } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { FingerprintData } from '@shared/services/auth';
import { AppConfig } from '@app/app.config';
import { LOCAL_STORAGE_KEY, StorageService } from '@app/core';

@Injectable({ providedIn: 'root' })
export class FingerprintService {
    private setStatus$ = new ReplaySubject<boolean>();
    private method: () => Promise<FingerprintData> = null;

    isReady$: Observable<boolean> = this.setStatus$.asObservable();

    private readonly appConfig = inject(AppConfig);
    private readonly storageService = inject(StorageService);

    initMethod(method) {
        if (this.method) {
            throw new Error('Method already initialized');
        }

        this.method = method;
        this.setStatus$.next(true);
    }

    getVisitorData(): Observable<FingerprintData> {
        if (!this.method) {
            throw new Error('Method is not initialized');
        }

        return from(this.method()).pipe(
            map((data) => ({
                visitorId: data.visitorId,
                requestId: data.requestId,
            })),
            catchError((error) => {
                console.error('FingerprintJS get error', error);
                return of(null);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    getFingerprintRequestId(userIdentifier?: string): Observable<string | null> {
        if (this.isFingerprintExcludedUser(userIdentifier)) {
            return of(null);
        }

        const cachedRequestId = this.getCachedFingerprintRequestId();
        if (cachedRequestId) {
            return of(cachedRequestId);
        }

        return this.isReady$.pipe(switchMap(() => this.getVisitorData())).pipe(
            map((fingerprintData) => fingerprintData?.requestId ?? null),
            tap((requestId) => this.setCachedFingerprintRequestId(requestId)),
            catchError(() => of(null)),
        );
    }

    private getCachedFingerprintRequestId(): string | null {
        const cachedRequestId = this.storageService.get<string>(LOCAL_STORAGE_KEY.FP_REQUEST_ID, null);
        if (!cachedRequestId) {
            return null;
        }

        const fingerprintTtl = this.storageService.get<number>(LOCAL_STORAGE_KEY.FP_CAPTURED_TIMESTAMP);
        const ttl = (this.appConfig.settings?.settings?.fingerprint_ttl as number) ?? 3600; // Default to 1 hour
        const isExpired = Date.now() - fingerprintTtl > ttl * 1000;

        if (isExpired) {
            this.storageService.remove(LOCAL_STORAGE_KEY.FP_REQUEST_ID);
            this.storageService.remove(LOCAL_STORAGE_KEY.FP_CAPTURED_TIMESTAMP);
            return null;
        }

        return cachedRequestId;
    }

    private setCachedFingerprintRequestId(requestId: string | null): void {
        if (!requestId) {
            return;
        }

        this.storageService.set(LOCAL_STORAGE_KEY.FP_REQUEST_ID, requestId);
        this.storageService.set(LOCAL_STORAGE_KEY.FP_CAPTURED_TIMESTAMP, Date.now());
    }

    private isFingerprintExcludedUser(userIdentifier?: string): boolean {
        if (!userIdentifier) {
            return false;
        }

        const excludedUsers = (this.appConfig.settings?.settings?.fingerprint_exclude_users as string[]) || [];
        return Boolean(excludedUsers.find((user) => user === userIdentifier));
    }
}
